import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import PagesTreeView from '../../../components/treeViews/PagesTreeView';
import PageDetailView from './PageDetailView';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import { usePagesApi } from '../../../hooks/usePagesApi';
import AddNewButton from '../../../components/common/AddButton';
import { confirmDelete } from '../../../utils/sweetAlertUtils';
import { MAX_DROPDOWN_PAGE_SIZE } from '../../../config/config';
import { PagesDTO } from '../../../services/apiClient';

const normId = (g?: string | null) => (g || '').toLowerCase();

const PagesList: React.FC = () => {
  const { search, remove, movePage, isLoading } = usePagesApi();
  const [items, setItems] = useState<PagesDTO[]>([]);
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const navigate = useNavigate();
  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Pages', href: '#' },
  ];

  // Fetch the full set once — the tree is built client-side from ParentPageId.
  const fetchItems = async () => {
    try {
      const response = await search({
        filters: {},
        page: 1,
        pageSize: MAX_DROPDOWN_PAGE_SIZE,
        sortBy: 'Title',
        sortDescending: false,
      });
      setItems(response?.data?.results || []);
    } catch (error) {
      console.error('Error fetching Pages:', error);
    }
  };

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async (dto: PagesDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await remove(dto.id || '');
      await fetchItems();
      if (normId(dto.id) === normId(selectedId)) {
        setSelectedId(null);
      }
    } catch (error) {
      console.error('Error deleting Page:', error);
    }
  };

  // Drag-and-drop re-parent: persist the move, then refetch so the tree (and slug paths) refresh.
  const handleMove = async (nodeId: string, newParentId: string | null) => {
    try {
      await movePage(nodeId, newParentId);
      await fetchItems();
    } catch (error) {
      console.error('Error moving Page:', error);
    }
  };

  // When searching, keep matching pages plus their ancestors so the hierarchy stays intact.
  const visibleItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;

    const byId = new Map(items.map((i) => [normId(i.id), i]));
    const keep = new Set<string>();

    items.forEach((item) => {
      if ((item.title || '').toLowerCase().includes(q)) {
        let current: PagesDTO | undefined = item;
        while (current && !keep.has(normId(current.id))) {
          keep.add(normId(current.id));
          current = current.parentPageId
            ? byId.get(normId(current.parentPageId))
            : undefined;
        }
      }
    });

    return items.filter((i) => keep.has(normId(i.id)));
  }, [items, query]);

  const selectedPage = useMemo(
    () => items.find((i) => normId(i.id) === normId(selectedId)) || null,
    [items, selectedId]
  );

  const parentTitle = useMemo(() => {
    if (!selectedPage?.parentPageId) return undefined;
    return items.find(
      (i) => normId(i.id) === normId(selectedPage.parentPageId)
    )?.title;
  }, [items, selectedPage]);

  const renderDetail = () => {
    if (!selectedPage) {
      return (
        <Card title="Page Details">
          <p className="py-8 text-center text-gray-400">
            Select a page from the tree to view its details.
          </p>
        </Card>
      );
    }

    return (
      <PageDetailView
        page={selectedPage}
        parentTitle={parentTitle}
        onEdit={() => navigate(`/admin/pages/${selectedPage.id}`)}
        onDelete={() => handleDelete(selectedPage)}
      />
    );
  };

  return (
    <AdminLayout
      activeMenuItem="/admin/pages"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
      requiredPermission="Content.Page.View"
    >
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-12 lg:col-span-6">
          <Card
            title="Pages"
            subtitle={`Total Records: ${items.length}`}
            headerActions={
              <AddNewButton onClick={() => navigate('/admin/pages/create')} />
            }
          >
            <FormField
              id="title"
              label="Search by Title"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter pages by title"
            />

            {query.trim() && (
              <div className="mb-3">
                <Button variant="secondary" onClick={() => setQuery('')}>
                  Clear Search
                </Button>
              </div>
            )}

            <PagesTreeView
              data={visibleItems}
              selectedId={selectedId}
              onSelect={(dto) => setSelectedId(dto.id || null)}
              onDelete={handleDelete}
              onMove={query.trim() ? undefined : handleMove}
              expandAll={!!query.trim()}
              isLoading={isLoading && items.length === 0}
            />
          </Card>
        </div>

        {/* Sticky on large screens so the detail panel stays in view while the tree scrolls. */}
        <div className="col-span-12 lg:col-span-6 lg:sticky lg:top-0 lg:self-start lg:max-h-screen lg:overflow-y-auto">
          {renderDetail()}
        </div>
      </div>
    </AdminLayout>
  );
};

export default PagesList;
